import api from '../services/api';

// Cache for storing lookup data to avoid repeated API calls
let lookupCache = {};

/**
 * Fetch lookups from the API
 * @param {string} tag - Optional tag to filter lookups
 * @param {string} name - Optional name to get a specific lookup
 * @returns {Promise<Object>} - Object with lookup data
 */
export const fetchLookups = async (tag = null, name = null) => {
  try {
    // Build the query string
    let url = '/lookups';
    if (tag) url += `?tag=${tag}`;
    if (name) url += tag ? `&name=${name}` : `?name=${name}`;
    
    // Check if we already have this data in cache
    const cacheKey = url;
    if (lookupCache[cacheKey]) {
      return lookupCache[cacheKey];
    }
    
    // Fetch from API if not in cache
    const response = await api.get(url);
    
    // Process the response
    const lookupData = {};
    if (response.data && response.data.data) {
      response.data.data.forEach(lookup => {
        lookupData[lookup.name] = lookup.choices;
      });
    }
    
    // Store in cache
    lookupCache[cacheKey] = lookupData;
    
    return lookupData;
  } catch (error) {
    console.error('Error fetching lookups:', error);
    return {};
  }
};

/**
 * Get the display value for a lookup field
 * @param {Object} lookups - The lookups object
 * @param {string} fieldName - The name of the lookup field
 * @param {number|string} value - The value to look up
 * @param {string} defaultValue - Default value to return if not found
 * @returns {string} - The display value
 */
export const getDisplayValue = (lookups, fieldName, value, defaultValue = 'Not specified') => {
  if (!value || !lookups[fieldName]) return defaultValue;
  
  const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
  
  if (isNaN(numValue)) return value; // If it's not a number, return as is
  
  const option = lookups[fieldName].find(opt => opt.id === numValue);
  return option ? option.value : defaultValue;
};

export default {
  fetchLookups,
  getDisplayValue
};
